import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Database, Bug, Hourglass } from 'lucide-react'
import { appRunLogApi, type AppRunWriteSource } from '@papan/files/client/app-run-log'
import { appWriteFailureKeys } from '@papan/files/client/queries'

/** A run whose saving took at least this long is worth a look (shown with where its time went). */
export const SLOW_WRITE_MS = 10_000

/** "850 ms" · "1.2 s" · "63 s". */
export function formatWriteMs(ms: number): string {
  if (ms < 1000) return `${Math.max(0, Math.round(ms))} ms`
  return ms < 10_000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms / 1000)} s`
}

function when(at: number): string {
  const d = new Date(at)
  return d.toDateString() === new Date().toDateString() ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : d.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
}

/** What needs the editor's attention: a source whose last run's data wasn't saved (not dismissed), or one whose
 *  saving was slow. Everything healthy stays out of the list — it would only grow. `exists(runKey)`: false for a
 *  trigger / schedule that's gone (its leftover row is shown only if it failed — that data is still missing). */
export function writesNeedingAttention(sources: readonly AppRunWriteSource[], exists: (runKey: string) => boolean): AppRunWriteSource[] {
  return sources.filter((s) => (s.outcome !== 'landed' ? !s.dismissed : exists(s.runKey) && (s.writeMs ?? 0) >= SLOW_WRITE_MS))
}

/**
 * The app settings' "Saving data" section — shown only when something needs attention. A script's saves to the app's
 * database happen AFTER it finishes, so a failure there is invisible to the script and to whoever ran it: this is where
 * the app's editors see it (the dot on the taskbar, the app shell and the editor lead here). A failure clears by itself
 * when that trigger / schedule / script's next run saves its data; Dismiss hides it until a run fails again.
 */
export function AppDatabaseWrites({ folderId, sources, labelFor, onOpenDebugLog }: {
  folderId: string
  /** Already filtered by `writesNeedingAttention`. */
  sources: readonly AppRunWriteSource[]
  /** A source's name as the settings list it (the trigger's or schedule's name), else null for the generic one. */
  labelFor: (runKey: string) => string | null
  onOpenDebugLog: () => void
}) {
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const failed = sources.filter((s) => s.outcome !== 'landed')

  const dismiss = async (runKey?: string) => {
    setBusy(true)
    setError(null)
    try {
      await appRunLogApi.dismissWriteFailure(folderId, runKey)
      await queryClient.invalidateQueries({ queryKey: appWriteFailureKeys.all })
    } catch {
      setError("Couldn't dismiss it — you may no longer be able to edit this app.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <section id="app-database-writes" data-testid="app-database-writes">
      <div className="flex items-center gap-2 mb-1">
        <Database size={16} className="themed-icon" />
        <h2 className="text-sm font-semibold themed-text">Saving data</h2>
        <div className="flex-1" />
        {failed.length > 1 && (
          <button onClick={() => void dismiss()} disabled={busy} className="text-xs themed-text-muted hover:themed-text disabled:opacity-40">Dismiss all</button>
        )}
      </div>
      <p className="text-xs themed-text-muted mb-3">
        This app&apos;s scripts save to its database after they finish. These need a look.
      </p>
      {error && <div className="text-xs text-red-400 mb-2">{error}</div>}
      <ul className="space-y-1">
        {sources.map((s) => {
          const label = labelFor(s.runKey) ?? describeSource(s)
          const isFailed = s.outcome !== 'landed'
          const slowest = s.slowestOps[0]
          return (
            <li key={s.runKey} data-run-key={s.runKey} className={`px-3 py-2 rounded ${isFailed ? 'bg-amber-500/10 border border-amber-500/30' : 'themed-bg-secondary'}`}>
              <div className="flex items-start gap-2">
                {isFailed
                  ? <AlertTriangle size={14} className="shrink-0 mt-0.5 text-amber-400" />
                  : <Hourglass size={14} className="shrink-0 mt-0.5 themed-icon" />}
                <div className="flex-1 min-w-0">
                  <div className="themed-text text-sm truncate">{label}</div>
                  <div className="text-[11px] themed-text-muted">
                    {isFailed ? `${headingFor(s)} · ${when(s.at)}` : `Its last run took ${formatWriteMs(s.writeMs ?? 0)} to save · ${when(s.at)}`}
                  </div>
                  {isFailed && s.reason && <div className="text-[11px] text-amber-300/90 mt-0.5 break-words">{s.reason}</div>}
                  {!isFailed && slowest && (
                    <div className="text-[11px] themed-text-muted mt-0.5">Most of the time went to “{slowest.op}”{slowest.count > 1 ? ` (run ${slowest.count} times)` : ''}.</div>
                  )}
                </div>
                {isFailed && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={onOpenDebugLog} className="inline-flex items-center gap-1 text-xs themed-text-muted hover:themed-text" data-tooltip="The full details, in the debug log">
                      <Bug size={12} /> Details
                    </button>
                    <button onClick={() => void dismiss(s.runKey)} disabled={busy} className="text-xs themed-text-muted hover:themed-text disabled:opacity-40">Dismiss</button>
                  </div>
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

/** The row's one-line heading — true for every outcome a person can read without the reason under it. */
function headingFor(s: AppRunWriteSource): string {
  // every step refused (`partialOrLanded`'s "None of this run's data was saved…") vs only some
  if (s.outcome === 'partial') return s.reason?.startsWith("None of this run's data was saved") ? "Its last run's data was refused" : "Some of its last run's data was refused"
  // a run refused at the door (`recordRunNotStarted`, D3) — its reason starts with the same server-written words
  if (s.reason?.startsWith("This run didn't start")) return "Its last run didn't start"
  return "Its last run's data wasn't all saved"
}

/** A source's generic name when the settings don't list it (a script run by hand, a trigger since deleted). */
function describeSource(s: AppRunWriteSource): string {
  const i = s.runKey.indexOf(':')
  const kind = s.runKey.slice(0, i)
  const rest = s.runKey.slice(i + 1)
  if (kind === 'script') return `Running ${rest}`
  if (kind === 'hook') return `Automation → ${rest}`
  if (kind === 'schedule') return `A schedule${s.scriptPath ? ` → ${s.scriptPath}` : ''}`
  if (kind === 'trigger') return `A trigger${s.scriptPath ? ` → ${s.scriptPath}` : ''}`
  return s.runKey
}
